import React, { useState } from 'react';
import AdminPasswordField from './AdminPasswordField';
import { PASSWORD_MIN_LENGTH } from '../../utils/passwordStrength';

/**
 * Modale de réinitialisation de mot de passe par un administrateur.
 * Applique la politique NIST 2024 (min 10 + HIBP + indicateur de force) via AdminPasswordField.
 * Remplace les anciens window.prompt (sans aucun contrôle de robustesse).
 *
 * Le parent fournit `onSubmit(password)` qui doit retourner { ok: bool, error?: string }.
 */
const ResetPasswordModal = ({ userName, onClose, onSubmit }) => {
  const [pwd, setPwd] = useState('');
  const [validity, setValidity] = useState({ tooShort: true, pwned: false, checking: false });
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');

  const canSubmit =
    pwd.length >= PASSWORD_MIN_LENGTH &&
    !validity.tooShort &&
    !validity.pwned &&
    !validity.checking &&
    !saving;

  const submit = async () => {
    if (!canSubmit) return;
    setSaving(true);
    setError('');
    try {
      const res = await onSubmit(pwd);
      if (res?.ok) {
        setSuccess('Mot de passe réinitialisé avec succès.');
        setTimeout(onClose, 1200);
      } else {
        setError(res?.error || 'Erreur lors de la réinitialisation.');
      }
    } catch {
      setError('Erreur de connexion.');
    } finally {
      setSaving(false);
    }
  };

  return (
    <div
      className="fixed inset-0 bg-black/50 flex items-center justify-center z-[60] p-4"
      onClick={onClose}
      data-testid="reset-password-modal"
    >
      <div className="bg-white rounded-2xl shadow-xl w-full max-w-md" onClick={(e) => e.stopPropagation()}>
        <div className="bg-gradient-to-r from-blue-600 to-indigo-600 px-6 py-4 rounded-t-2xl flex items-center gap-2">
          <i className="bi bi-key text-white"></i>
          <h2 className="text-lg font-bold text-white">Réinitialiser le mot de passe</h2>
        </div>
        <div className="p-6 space-y-4">
          <p className="text-sm text-slate-600">
            Définir un nouveau mot de passe pour <strong>{userName}</strong>.
          </p>
          {error && (
            <div className="bg-red-50 border border-red-200 rounded-lg p-3 text-red-700 text-sm" data-testid="reset-password-error">
              {error}
            </div>
          )}
          {success && (
            <div className="bg-emerald-50 border border-emerald-200 rounded-lg p-3 text-emerald-700 text-sm" data-testid="reset-password-success">
              {success}
            </div>
          )}
          <AdminPasswordField
            value={pwd}
            onChange={setPwd}
            onValidityChange={setValidity}
            label="Nouveau mot de passe"
            testid="reset-password"
            autoFocus
          />
          <p className="text-xs text-slate-400">
            <i className="bi bi-info-circle mr-1"></i>
            Minimum {PASSWORD_MIN_LENGTH} caractères, non compromis (vérifié via Have I Been Pwned).
          </p>
          <div className="flex justify-end gap-3 pt-2 border-t">
            <button
              type="button"
              onClick={onClose}
              className="px-4 py-2 text-slate-600 hover:bg-slate-100 rounded-lg transition"
              data-testid="reset-password-cancel"
            >
              Annuler
            </button>
            <button
              type="button"
              onClick={submit}
              disabled={!canSubmit}
              className="px-6 py-2 bg-blue-600 hover:bg-blue-700 text-white rounded-lg font-medium transition disabled:bg-slate-300 disabled:cursor-not-allowed flex items-center gap-2"
              data-testid="reset-password-submit"
            >
              {saving ? (
                <><i className="bi bi-arrow-repeat animate-spin"></i>Enregistrement…</>
              ) : (
                <><i className="bi bi-shield-check"></i>Réinitialiser</>
              )}
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};

export default ResetPasswordModal;
